import { COMPETITION_CATEGORY_LABELS, MATCH_TYPE_LABELS } from './labels';
import type {
  ApiCompetition,
  CompetitionStatus,
  DisciplineCode,
  TournamentProgress,
} from './types';

/**
 * Model widokowy listy turniejów klubu.
 *
 * Wszystko tutaj to czyste funkcje — filtrowanie, sortowanie, stronicowanie
 * i formatowanie robimy po stronie klienta, bo `GET /api/competitions?clubId=…`
 * zwraca całą listę naraz i nie przyjmuje parametrów wyszukiwania.
 */

/** Zakładka listy. Grupuje siedem statusów API w pięć kubełków z makiety. */
export type TournamentFilterKey =
  | 'all'
  | 'active'
  | 'upcoming'
  | 'completed'
  | 'draft'
  | 'cancelled';

/** Kolejność zakładek na pasku filtrów. */
export const TOURNAMENT_FILTERS: readonly TournamentFilterKey[] = [
  'all',
  'active',
  'upcoming',
  'completed',
  'draft',
  'cancelled',
] as const;

/**
 * Przypisanie statusu z API do zakładki.
 *
 * `AWAITING_PAYMENT` trafia do przyszłych, a nie do szkiców: rozgrywka jest już
 * skonfigurowana i czeka wyłącznie na opłatę, więc z punktu widzenia organizatora
 * zachowuje się jak zaplanowana.
 */
const STATUS_GROUP: Record<CompetitionStatus, Exclude<TournamentFilterKey, 'all'>> = {
  IN_PROGRESS: 'active',
  REGISTRATION_OPEN: 'upcoming',
  REGISTRATION_CLOSED: 'upcoming',
  AWAITING_PAYMENT: 'upcoming',
  DRAFT: 'draft',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

/** Zwraca zakładkę, do której należy rozgrywka o danym statusie. */
export function statusGroup(status: CompetitionStatus): Exclude<TournamentFilterKey, 'all'> {
  return STATUS_GROUP[status];
}

/** Kolejność grup na liście: najpierw to, co przed nami, na końcu zamknięte sprawy. */
const GROUP_ORDER: Record<Exclude<TournamentFilterKey, 'all'>, number> = {
  active: 0,
  upcoming: 1,
  draft: 2,
  completed: 3,
  cancelled: 4,
};

/** Grupy, w których bliższa data znaczy „pilniejsze” — sortujemy je rosnąco. */
const ASCENDING_GROUPS: ReadonlySet<string> = new Set(['active', 'upcoming', 'draft']);

/* --- Daty --- */

/** Milisekundy w dobie — do przeliczania różnicy dat na dni. */
const DAY_MS = 86_400_000;

/**
 * Parsuje datę z API (`yyyy-MM-dd`) na datę lokalną.
 *
 * Świadomie nie używamy `new Date(iso)` — to parsuje jako północ UTC i w naszej
 * strefie potrafi cofnąć dobę.
 */
export function parseApiDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Liczba pełnych dni od dzisiaj do podanej daty. Ujemna dla przeszłości. */
export function daysUntil(value: string | null | undefined, today: Date): number | null {
  const target = parseApiDate(value);
  if (!target) return null;
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  // Zaokrąglamy, bo doba zmiany czasu ma 23 albo 25 godzin.
  return Math.round((target.getTime() - start) / DAY_MS);
}

/** Etykieta wyprzedzenia — „Dziś”, „Jutro”, „Za 5 dni”. Dla przeszłości `null`. */
export function relativeDayLabel(days: number | null): string | null {
  if (days === null || days < 0) return null;
  if (days === 0) return 'Dziś';
  if (days === 1) return 'Jutro';
  return `Za ${days} dni`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Formatuje datę jako `24.05.2024`. */
export function formatDate(value: string | null | undefined): string | null {
  const date = parseApiDate(value);
  if (!date) return null;
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/**
 * Formatuje termin rozgrywki: `24.05 – 26.05.2024`.
 * Rok przy dacie początkowej pojawia się tylko wtedy, gdy termin przechodzi przez sylwestra.
 */
export function formatDateRange(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string {
  const start = parseApiDate(startDate);
  const end = parseApiDate(endDate);

  if (!start && !end) return '—';
  if (!start) return formatDate(endDate) ?? '—';
  if (!end || start.getTime() === end.getTime()) return formatDate(startDate) ?? '—';

  const startShort =
    start.getFullYear() === end.getFullYear()
      ? `${pad(start.getDate())}.${pad(start.getMonth() + 1)}`
      : formatDate(startDate);

  return `${startShort} – ${formatDate(endDate)}`;
}

/** Formatuje znacznik czasu z API (`Instant`) jako datę `01.05.2024`. */
export function formatInstant(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
}

/* --- Etykiety --- */

/** Etykiety dyscyplin z API — pełna lista enuma `Discipline`. */
export const DISCIPLINE_CODE_LABELS: Record<DisciplineCode, string> = {
  TENNIS: 'Tenis',
  PADEL: 'Padel',
  SQUASH: 'Squash',
  PICKLEBALL: 'Pickleball',
  BADMINTON: 'Badminton',
  FITNESS: 'Fitness',
};

/** Etykiety systemu rozgrywek z API (`CompetitionSystem`). */
export const COMPETITION_SYSTEM_LABELS: Record<string, string> = {
  PLAYOFF: 'Drabinka',
  GROUP_PLAYOFF: 'Grupy + Drabinka',
  SINGLE_GROUP: 'Każdy z każdym',
  CHALLENGE: 'Challenge',
};

/** Etykiety rodzaju rozgrywki z API (`CompetitionType`). */
export const COMPETITION_TYPE_LABELS: Record<string, string> = {
  TOURNAMENT: 'Turniej',
  LEAGUE: 'Liga',
};

/**
 * Podtytuł wiersza — „Singiel”, „Debel • Junior”.
 *
 * Bez rodzaju rozgrywki: ten ma własną kolumnę, więc powtarzanie go tutaj
 * tylko zabierałoby miejsce. Makieta pokazuje w tym miejscu podział na płeć
 * i rocznik („Debel Mężczyzn”, „U12”), ale API nie ma takich pól — składamy
 * podtytuł z tego, co faktycznie wraca.
 */
export function tournamentSubtitle(competition: ApiCompetition): string {
  const parts = [MATCH_TYPE_LABELS[competition.matchType] ?? competition.matchType];
  if (competition.category) {
    parts.push(COMPETITION_CATEGORY_LABELS[competition.category] ?? competition.category);
  }
  return parts.join(' • ');
}

/** Etykieta kolumny „Rodzaj” — „Turniej”, „Liga”. */
export function competitionTypeLabel(competition: ApiCompetition): string {
  return COMPETITION_TYPE_LABELS[competition.type] ?? competition.type;
}

/** Etykieta systemu rozgrywek — „Drabinka”, „Każdy z każdym”. */
export function competitionSystemLabel(competition: ApiCompetition): string {
  return COMPETITION_SYSTEM_LABELS[competition.system] ?? competition.system;
}

/** Etykieta kolumny „Kategoria”. */
export function categoryLabel(competition: ApiCompetition): string {
  if (!competition.category) return '—';
  return COMPETITION_CATEGORY_LABELS[competition.category] ?? competition.category;
}

/* --- Filtrowanie i sortowanie --- */

export interface TournamentListFilters {
  /** Aktywna zakładka statusu. */
  filter: TournamentFilterKey;
  /** Fraza szukana w nazwie i opisie. */
  search: string;
  /** Dyscyplina albo `all`. */
  discipline: DisciplineCode | 'all';
  /** Kategoria albo `all`. */
  category: string;
}

/** Stan filtrów, od którego zaczyna widok. */
export const DEFAULT_TOURNAMENT_FILTERS: TournamentListFilters = {
  filter: 'all',
  search: '',
  discipline: 'all',
  category: 'all',
};

/** Normalizuje frazę do porównań — bez wielkości liter i polskich znaków. */
function normalize(value: string): string {
  // Rozkład NFD odcina ogonki i kreski od liter (ą -> a + ogonek), a klasa
  // znaków diakrytycznych U+0300–U+036F je usuwa. „ł” nie ma rozkładu,
  // więc podmieniamy je osobno.
  return value
    .toLocaleLowerCase('pl-PL')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l');
}

/** Sprawdza, czy rozgrywka pasuje do frazy wyszukiwania. */
export function matchesSearch(competition: ApiCompetition, search: string): boolean {
  const needle = normalize(search.trim());
  if (!needle) return true;
  const haystack = normalize(
    [competition.name, competition.description ?? '', competition.city ?? ''].join(' '),
  );
  return haystack.includes(needle);
}

/** Liczniki przy zakładkach — liczone z pełnej listy, niezależnie od pozostałych filtrów. */
export function countByFilter(
  competitions: readonly ApiCompetition[],
): Record<TournamentFilterKey, number> {
  const counts: Record<TournamentFilterKey, number> = {
    all: competitions.length,
    active: 0,
    upcoming: 0,
    completed: 0,
    draft: 0,
    cancelled: 0,
  };
  for (const competition of competitions) {
    counts[statusGroup(competition.status)] += 1;
  }
  return counts;
}

/**
 * Porządek listy: najpierw sprawy bieżące (trwające, zaplanowane, szkice)
 * od najbliższej daty, potem zamknięte — od ostatniej. Rozgrywki bez terminu
 * lądują na końcu swojej grupy.
 */
export function compareTournaments(a: ApiCompetition, b: ApiCompetition): number {
  const groupA = statusGroup(a.status);
  const groupB = statusGroup(b.status);
  if (groupA !== groupB) return GROUP_ORDER[groupA] - GROUP_ORDER[groupB];

  const dateA = parseApiDate(a.startDate)?.getTime() ?? null;
  const dateB = parseApiDate(b.startDate)?.getTime() ?? null;
  if (dateA === null && dateB === null) return a.name.localeCompare(b.name, 'pl-PL');
  if (dateA === null) return 1;
  if (dateB === null) return -1;
  if (dateA === dateB) return a.name.localeCompare(b.name, 'pl-PL');

  return ASCENDING_GROUPS.has(groupA) ? dateA - dateB : dateB - dateA;
}

/** Stosuje wszystkie filtry i porządkuje wynik. */
export function filterTournaments(
  competitions: readonly ApiCompetition[],
  filters: TournamentListFilters,
): ApiCompetition[] {
  return competitions
    .filter((competition) => {
      if (filters.filter !== 'all' && statusGroup(competition.status) !== filters.filter) {
        return false;
      }
      if (filters.discipline !== 'all' && competition.discipline !== filters.discipline) {
        return false;
      }
      if (filters.category !== 'all' && (competition.category ?? '') !== filters.category) {
        return false;
      }
      return matchesSearch(competition, filters.search);
    })
    .sort(compareTournaments);
}

/** Liczba filtrów zawężających poza zakładką — do znacznika przy przycisku „Filtry”. */
export function activeFilterCount(filters: TournamentListFilters): number {
  let count = 0;
  if (filters.discipline !== 'all') count += 1;
  if (filters.category !== 'all') count += 1;
  return count;
}

/* --- Stronicowanie --- */

/** Dostępne rozmiary strony. */
export const PAGE_SIZES = [10, 25, 50] as const;

export interface PageSlice<T> {
  /** Elementy bieżącej strony. */
  items: T[];
  /** Numer bieżącej strony po skorygowaniu do zakresu, liczony od 1. */
  page: number;
  pageCount: number;
  /** Numer pierwszego elementu strony, liczony od 1. Zero dla pustej listy. */
  from: number;
  to: number;
  total: number;
}

/** Wycina stronę wyników i podaje dane do podpisu „1–10 z 24”. */
export function paginate<T>(items: readonly T[], page: number, pageSize: number): PageSlice<T> {
  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const start = (safePage - 1) * pageSize;
  const slice = items.slice(start, start + pageSize);

  return {
    items: slice,
    page: safePage,
    pageCount,
    from: total === 0 ? 0 : start + 1,
    to: start + slice.length,
    total,
  };
}

/* --- Zapisy --- */

/** Stan zapisów wyprowadzony ze statusu rozgrywki. */
export type RegistrationState = 'open' | 'closed' | 'none';

/**
 * Czy zapisy do rozgrywki są otwarte.
 *
 * API nie ma osobnego pola — stan zapisów wynika wprost z cyklu życia:
 * otwiera je `open-registration`, a zamyka `close-registration` albo start
 * rozgrywki. Szkic i rozgrywka czekająca na opłatę zapisów jeszcze nie miały,
 * anulowana już ich nie ma.
 */
export function registrationState(status: CompetitionStatus): RegistrationState {
  switch (status) {
    case 'REGISTRATION_OPEN':
      return 'open';
    case 'REGISTRATION_CLOSED':
    case 'IN_PROGRESS':
    case 'COMPLETED':
      return 'closed';
    default:
      return 'none';
  }
}

/* --- Kopiowanie --- */

/**
 * Statusy, od których organizator może skopiować turniej.
 *
 * Kopiowanie pojawia się dopiero z otwarciem zapisów — wcześniej (szkic, oczekiwanie
 * na opłatę) ustawienia wciąż się zmieniają, więc kopia powielałaby stan roboczy.
 * Później dostępne jest zawsze: zamknięte zapisy, turniej w toku i zakończony kopiuje
 * się tak samo. Anulowany zostaje poza listą — nie wiadomo, czy zapisy w ogóle ruszyły.
 */
const COPYABLE_STATUSES: ReadonlySet<CompetitionStatus> = new Set<CompetitionStatus>([
  'REGISTRATION_OPEN',
  'REGISTRATION_CLOSED',
  'IN_PROGRESS',
  'COMPLETED',
]);

/** Czy z turnieju o danym statusie można zrobić kopię. */
export function canCopyTournament(status: CompetitionStatus): boolean {
  return COPYABLE_STATUSES.has(status);
}

/** Nazwa podpowiadana w oknie kopiowania — organizator zwykle ją nadpisuje. */
export function copySuggestedName(name: string): string {
  return `${name} (kopia)`;
}

/** Etykiety kolumny „Zapisy". */
export const REGISTRATION_LABELS: Record<RegistrationState, string> = {
  open: 'Otwarte',
  closed: 'Zamknięte',
  none: 'Nieotwarte',
};

/** Ton etykiety stanu zapisów. */
export const REGISTRATION_TONE: Record<RegistrationState, 'success' | 'neutral'> = {
  open: 'success',
  closed: 'neutral',
  none: 'neutral',
};

/* --- Postęp i uczestnicy --- */

/**
 * Indeksuje postęp rozgrywek po identyfikatorze.
 *
 * `CompetitionResponse` nie niesie ani liczby zapisanych, ani liczby rozegranych
 * meczów, więc oba bierzemy z agregatu `GET /competitions/club/{clubId}/dashboard`.
 * Ten obejmuje rozgrywki trwające, zaplanowane i zakończone — dla anulowanych
 * wpisu nie ma i kolumny pokazują kreskę zamiast zmyślonego zera.
 */
export function indexTournamentProgress(
  tournaments: readonly TournamentProgress[] | undefined,
): Map<string, TournamentProgress> {
  return new Map((tournaments ?? []).map((item) => [item.competitionId, item]));
}
